import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton
} from '@mui/material';
import {
  Delete as DeleteIcon,
  RadioButtonUnchecked as UnreadIcon,
  CheckCircle as ReadIcon
} from '@mui/icons-material';
import moment from 'moment';
import { resolveNotificationMessage } from '../utils/notificationMessages';
import { getNotificationVisual } from '../utils/notificationVisuals';
import { BG_PURPLE_TINT, PURPLE_MAIN, RADIUS_TILE, CARD_SHADOW } from './bento/bentoTokens';

const NotificationItem = ({
  notification,
  onClick,
  onDelete,
  onMarkAsRead,
  showActions = true,
  compact = false
}) => {
  const { Icon, badgeLabel, color, bg, border } = getNotificationVisual(notification);

  const formatTime = (timestamp) => {
    if (compact) {
      return moment(timestamp).fromNow();
    }
    return moment(timestamp).format('MMM DD, YYYY - HH:mm');
  };

  const handleClick = () => {
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
    if (onClick) {
      onClick(notification);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(notification.id);
    }
  };

  const handleToggleRead = (e) => {
    e.stopPropagation();
    if (onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <Card
      sx={{
        mb: 1,
        borderRadius: RADIUS_TILE,
        backgroundColor: notification.isRead ? 'transparent' : BG_PURPLE_TINT,
        border: notification.isRead ? '1px solid rgba(0, 0, 0, 0.12)' : `2px solid ${PURPLE_MAIN}`,
        '&:hover': {
          backgroundColor: notification.isRead
            ? 'rgba(0, 0, 0, 0.04)'
            : 'rgba(109, 40, 217, 0.12)',
          cursor: onClick ? 'pointer' : 'default',
          transform: 'translateY(-1px)',
          boxShadow: CARD_SHADOW
        },
        transition: 'all 0.2s ease-in-out'
      }}
      onClick={handleClick}
    >
      <CardContent sx={{ p: compact ? 2 : 3, '&:last-child': { pb: compact ? 2 : 3 } }}>
        <Box display="flex" alignItems="flex-start" gap={2}>
          {/* Notification Icon */}
          <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: '11px', background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon sx={{ fontSize: 18, color }} />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box sx={{ flex: 1, pr: 1, minWidth: 0 }}>
                <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color, background: bg, px: '8px', py: '2px', borderRadius: '999px', mr: 1, display: 'inline-block', mb: 0.5 }}>
                  {badgeLabel}
                </Box>
                <Typography
                  variant={compact ? "body2" : "body1"}
                  sx={{
                    fontWeight: notification.isRead ? 'normal' : 'bold',
                    wordBreak: 'break-word'
                  }}
                >
                  {resolveNotificationMessage(notification)}
                </Typography>
              </Box>

              {/* Read Status & Time */}
              <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {formatTime(notification.timestamp)}
                </Typography>
                {!notification.isRead && (
                  <Chip
                    label="New"
                    size="small"
                    color="secondary"
                    sx={{ height: 16, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            </Box>

            {/* Package and Station Info */}
            <Box display="flex" flexWrap="wrap" gap={0.5} mb={compact ? 0 : 1}>
              {notification.tracking && (
                <Chip 
                  label={`Package: ${notification.tracking}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
              {notification.station && (
                <Chip 
                  label={`${notification.station.name}${notification.station.finalStation ? ' (Final)' : ''}`}
                  size="small"
                  variant="outlined"
                  color={notification.station.finalStation ? "success" : "default"}
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
              {notification.status && (
                <Chip 
                  label={notification.status}
                  size="small"
                  variant="outlined"
                  color={notification.status?.toLowerCase() === 'delivered' ? "success" : "default"}
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
            </Box>

            {/* Actions */}
            {showActions && (
              <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                {onMarkAsRead && (
                  <IconButton 
                    size="small" 
                    onClick={handleToggleRead}
                    title={notification.isRead ? "Mark as unread" : "Mark as read"}
                    sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                  >
                    {notification.isRead ? <UnreadIcon fontSize="small" /> : <ReadIcon fontSize="small" />}
                  </IconButton>
                )}
                {onDelete && (
                  <IconButton 
                    size="small" 
                    onClick={handleDelete}
                    title="Delete notification"
                    sx={{ opacity: 0.7, '&:hover': { opacity: 1, color: 'error.main' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NotificationItem;